import { useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/brand";
import { toast } from "sonner";
import { Upload, Trash2, FileArchive, ShieldCheck, FileText } from "lucide-react";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function FilesTab({ clientId, services }) {
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", version: "1.0", price: "0", requires_terms: false, service_id: "" });
  const [uploading, setUploading] = useState(false);

  const load = () => api.get(`/clients/${clientId}/files`).then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, [clientId]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file"); return; }
    if (!form.name) { toast.error("File label is required"); return; }
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
    setUploading(true);
    try {
      await api.post(`/clients/${clientId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded securely");
      setForm({ name: "", description: "", version: "1.0", price: "0", requires_terms: false, service_id: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = (id) => {
    if (!window.confirm("Delete this file? Any download links are revoked.")) return;
    api.delete(`/files/${id}`).then(() => { toast.success("Deleted"); load(); }).catch(() => toast.error("Failed"));
  };

  return (
    <div className="space-y-5" data-testid="client-files">
      <div className="bg-white border border-[#E2EBF0] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1"><FileArchive className="h-4 w-4 text-[#10B981]" />
          <h3 className="font-heading font-semibold text-[#0D3B2D]">Upload a file package</h3></div>
        <p className="text-xs text-[#4A635A] mb-4">Source ZIPs, code, plugins, SQL dumps, backups, docs. Stored in private object storage with SHA-256. Clients request a download; you approve and set any fee.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Label *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Website source v2" data-testid="file-name-input" className="bg-white border-[#E2EBF0]" /></div>
          <div><Label>Version</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="file-version-input" className="bg-white border-[#E2EBF0]" /></div>
          <div><Label>Linked service (optional)</Label>
            <select value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}
              data-testid="file-service-select" className="w-full h-10 mt-1 bg-white border border-[#E2EBF0] rounded-md px-3 text-sm text-[#0D3B2D]">
              <option value="">None</option>
              {services.map((s) => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}
            </select>
          </div>
          <div><Label>Default release fee (USD)</Label><Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="file-price-input" className="bg-white border-[#E2EBF0] font-mono" /></div>
          <div className="sm:col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="file-desc-input" className="bg-white border-[#E2EBF0]" /></div>
          <label className="flex items-center gap-2 text-sm text-[#0D3B2D] sm:col-span-2">
            <input type="checkbox" checked={form.requires_terms} onChange={(e) => setForm({ ...form, requires_terms: e.target.checked })} data-testid="file-terms-check" />
            Require transfer terms acceptance before download
          </label>
          <div className="sm:col-span-2"><Label>File</Label>
            <input ref={fileRef} type="file" data-testid="file-input"
              className="block w-full text-sm text-[#4A635A] mt-1 file:mr-3 file:rounded-lg file:border-0 file:bg-[#0D3B2D] file:text-white file:px-3 file:py-2" />
          </div>
        </div>
        <Button onClick={upload} disabled={uploading} data-testid="file-upload-btn"
          className="mt-4 bg-[#67F2B3] hover:bg-[#4DE6A3] text-[#071A13] font-semibold rounded-lg">
          <Upload className="h-4 w-4 mr-1" /> {uploading ? "Uploading…" : "Upload file"}
        </Button>
      </div>

      <div className="bg-white border border-[#E2EBF0] rounded-xl p-5" data-testid="client-files-list">
        <h3 className="font-heading font-semibold text-[#0D3B2D] mb-3">Files ({files.length})</h3>
        {files.length === 0 ? <p className="text-sm text-[#4A635A]">No files uploaded for this client yet.</p> : (
          <ul className="space-y-2">
            {files.map((f) => (
              <li key={f.file_id} className="flex items-center justify-between gap-3 border border-[#E2EBF0] rounded-lg px-3 py-2.5" data-testid={`client-file-${f.file_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {f.kind === "backup" ? <FileArchive className="h-4 w-4 text-[#4A635A]" /> : <FileText className="h-4 w-4 text-[#4A635A]" />}
                    <span className="text-sm font-medium text-[#0D3B2D] truncate">{f.name}</span>
                    {f.version && <span className="text-[11px] text-[#4A635A]">v{f.version}</span>}
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-[#F4FFF9] border border-[#E2EBF0] text-[#4A635A]">{f.ext?.replace(".", "")}</span>
                    {f.integrity_verified && <span className="text-[10px] inline-flex items-center gap-1 text-[#10B981]" title={f.sha256}><ShieldCheck className="h-3 w-3" /> SHA-256</span>}
                  </div>
                  <div className="text-[11px] text-[#4A635A] mt-0.5">{fmtSize(f.size || 0)} · {f.price > 0 ? money(f.price) : "free"}{f.requires_terms ? " · terms required" : ""}</div>
                </div>
                <button onClick={() => remove(f.file_id)} data-testid={`file-delete-${f.file_id}`} className="text-red-600 shrink-0"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
